import React, { useEffect, useRef, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import * as importApi from '../services/importApi';

const IMPORT_TYPES = {
  order: { label: 'Order Excel', description: 'Upload customer orders.', appendMode: false },
  stock: { label: 'Stock Excel', description: 'Upload current stock snapshot by location.', appendMode: false },
  dispatch: { label: 'Dispatch Excel', description: 'Upload dispatch records.', appendMode: true },
  production: { label: 'Production Excel', description: 'Upload production output records.', appendMode: true },
};

function StatusPill({ status }) {
  const colors = {
    completed: 'var(--color-success)',
    processing: 'var(--color-warning)',
    failed: 'var(--color-danger)',
    rolled_back: 'var(--color-text-muted)',
  };
  return (
    <span
      style={{
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
        color: colors[status] || 'var(--color-text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {status}
    </span>
  );
}

export default function ImportPage() {
  const { type } = useParams();
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => importApi.getImportHistory(type).then(setHistory);

  useEffect(() => {
    setPreview(null);
    setCommitted(null);
    setError(null);
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  if (!IMPORT_TYPES[type]) {
    return <Navigate to="/imports/order" replace />;
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(null);
    setCommitted(null);
    setUploading(true);
    try {
      const result = await importApi.uploadPreview(type, file);
      setPreview(result);
    } catch (err) {
      setError(err.message || 'Could not read this file.');
      setPreview(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCommit = async () => {
    if (!preview) return;
    if (!window.confirm(
      `This will DELETE all existing ${meta.label} data and replace it with this file's ${preview.accepted} row(s). ` +
      'This cannot be undone by rolling back afterward — rollback only removes what this import adds, it does not restore what gets replaced. Continue?',
    )) {
      return;
    }
    setCommitting(true);
    setError(null);
    try {
      const result = await importApi.commitImport(preview.importId);
      setCommitted(result);
      setPreview(null);
      await loadHistory();
    } catch (err) {
      setError(err.message || 'Import failed.');
    } finally {
      setCommitting(false);
    }
  };

  const handleRollback = async (importId) => {
    if (!window.confirm(
      'This removes the rows this import added. Because each import replaces all previous ' +
      'data of this type, this will NOT restore whatever was there before this import — it ' +
      'will leave this data type empty until you import again. Continue?',
    )) {
      return;
    }
    try {
      await importApi.rollbackImport(importId);
      await loadHistory();
    } catch (err) {
      setError(err.message || 'Rollback failed.');
    }
  };

  const meta = IMPORT_TYPES[type];

  return (
    <div>
      <h1>{meta.label}</h1>
      <p style={{ color: 'var(--color-text-secondary)' }}>{meta.description}</p>

      <Card title="Upload">
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-warning)', background: 'var(--color-surface-alt)', padding: 'var(--space-3)', borderRadius: 'var(--radius-sm)' }}>
          {meta.appendMode ? (
            <>
              ℹ Importing a new file adds to existing {meta.label} data — rows that exactly match
              ones already stored (same design, size, date, shade, and quantity) are automatically
              skipped, so uploading the same file twice is safe and won't create duplicates.
            </>
          ) : (
            <>
              ⚠ Importing a new file replaces all existing {meta.label} data — it does not add to it.
              Make sure this file contains everything you want kept, not just what's changed.
            </>
          )}
        </p>
        {error && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>{error}</p>
        )}
        {committed && (
          <div style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-sm)' }}>
            <p style={{ margin: 0 }}>
              Import completed — {committed.accepted} of {committed.totalRows} row
              {committed.totalRows === 1 ? '' : 's'} added.
            </p>
            {committed.skippedDuplicates > 0 && (
              <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)' }}>
                {committed.skippedDuplicates} row{committed.skippedDuplicates === 1 ? '' : 's'} already
                existed and {committed.skippedDuplicates === 1 ? 'was' : 'were'} skipped as duplicate
                — not added again.
              </p>
            )}
            {committed.rejected > 0 && (
              <p style={{ margin: '4px 0 0', color: 'var(--color-danger)' }}>
                {committed.rejected} row{committed.rejected === 1 ? '' : 's'} rejected — see the Error
                Report above.
              </p>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          disabled={uploading}
          aria-label={`Upload ${meta.label}`}
        />
        {uploading && <p style={{ color: 'var(--color-text-secondary)' }}>Reading file…</p>}

        {preview && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <div className="kpi-row" style={{ display: 'flex', gap: 'var(--space-5)', marginBottom: 'var(--space-3)' }}>
              <div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Total rows</div>
                <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)' }}>{preview.totalRows}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Accepted</div>
                <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-success)' }}>
                  {preview.accepted}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Merged</div>
                <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', color: preview.merged > 0 ? 'var(--color-info)' : 'var(--color-text-primary)' }}>
                  {preview.merged || 0}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Rejected</div>
                <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', color: preview.rejected > 0 ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
                  {preview.rejected}
                </div>
              </div>
            </div>

            {preview.merges && preview.merges.length > 0 && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <h3 style={{ fontSize: 'var(--font-size-sm)' }}>Merged rows</h3>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 0 }}>
                  These rows matched an earlier row exactly (same design, customer/location, date,
                  and quantity) — nothing was discarded, the quantities were combined into one row.
                </p>
                <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  {preview.merges.map((m, i) => (
                    <li key={i}>
                      Row {m.row}: {m.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {preview.errors.length > 0 && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <h3 style={{ fontSize: 'var(--font-size-sm)' }}>Errors</h3>
                <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)' }}>
                  {preview.errors.map((e, i) => (
                    <li key={i}>
                      Row {e.row} ({e.column}): {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button onClick={handleCommit} disabled={committing || preview.accepted === 0}>
              {committing ? 'Importing…' : `Confirm import (${preview.accepted} rows)`}
            </Button>
          </div>
        )}
      </Card>

      <Card title="Import History">
        <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
              <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>File</th>
              <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Rows</th>
              <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Merged</th>
              <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Status</th>
              <th style={{ padding: '8px 4px' }} />
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.import_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{h.file_name}</td>
                <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                  {h.accepted_rows}/{h.total_rows}
                </td>
                <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                  {h.merged_rows || 0}
                </td>
                <td style={{ padding: '8px 4px' }}>
                  <StatusPill status={h.status} />
                </td>
                <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                  {h.status === 'completed' && (
                    <Button variant="text" onClick={() => handleRollback(h.import_id)}>
                      Rollback
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                  No imports yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}
